import React from 'react';
import I18n from 'modules/i18n';
import Text from '@/components/ui/Text';
import { StandardNode } from '@/components/shared/Standards/types';
import { CheckboxState } from '../standardsFilterUtils';
import useCorrelationsStore from '../useCorrelationsStore';
import { useStandardsChildren } from '../hooks/useStandardsTree';
import { truncationRowAttrs } from '../TruncatedCorrelationsList';
import SeeMoreButton from './SeeMoreButton';
import StandardRow, { CHEVRON_PX, describeStandard, INDENT_PX } from './StandardRow';

interface StandardsTreeNodeProps {
  getCheckboxState: (id: number) => CheckboxState;
  grade: string | number | null;
  node: StandardNode;
  programId: number;
  stateId: number;
  onCheck: (id: number, checked: boolean) => void;
}

const StatusRow = ({ children, depth }: { children: React.ReactNode; depth: number }) => (
  <li
    className="tw-py-1"
    style={{ paddingLeft: `${depth * INDENT_PX + CHEVRON_PX}px` }}
    {...truncationRowAttrs()}
  >
    <Text className="tw-text-very-dark-grey" size="sm">{children}</Text>
  </li>
);

const StandardsTreeNode = ({ getCheckboxState, grade, node, programId, stateId, onCheck }: StandardsTreeNodeProps) => {
  const expanded = useCorrelationsStore(s => s.standardsExpandedIds.has(node.id));
  const toggleStandardExpanded = useCorrelationsStore(s => s.toggleStandardExpanded);
  const {
    fetchNextPage, hasNextPage, isError, isFetchingNextPage, isInitialLoading, nodes: children, totalCount,
  } = useStandardsChildren({
    enabled: expanded && node.has_children, grade, parentId: node.id, programId, stateId,
  });

  const loadedEmpty = node.has_children && totalCount === 0;
  const expandable = node.has_children && !loadedEmpty;
  const { depth, label } = describeStandard(node);

  return (
    <StandardRow
      checkboxState={getCheckboxState(node.id)}
      expandable={expandable}
      expanded={expanded}
      node={node}
      onCheck={onCheck}
      onToggle={() => toggleStandardExpanded(node.id)}
    >
      {isInitialLoading && <StatusRow depth={depth + 1}>{I18n.t('loading')}</StatusRow>}
      {isError && <StatusRow depth={depth + 1}>{I18n.t('error_loading_standards')}</StatusRow>}
      {children?.map(child => (
        <StandardsTreeNode
          key={child.id}
          getCheckboxState={getCheckboxState}
          grade={grade}
          node={child}
          programId={programId}
          stateId={stateId}
          onCheck={onCheck}
        />
      ))}
      {hasNextPage && (
        <SeeMoreButton
          label={label}
          loading={isFetchingNextPage}
          style={{ paddingLeft: `${(depth + 1) * INDENT_PX + CHEVRON_PX}px` }}
          onClick={() => fetchNextPage()}
        />
      )}
    </StandardRow>
  );
};

export default StandardsTreeNode;
